/**
 * Cross-platform flag renderer.
 *
 * Why this exists: Windows desktop (Win 10/11) deliberately ships its
 * Segoe UI Emoji font WITHOUT flag glyphs — emoji flags fall back to the
 * underlying two-letter regional indicators (e.g. 🇬🇧 → "GB", 🇦🇺 → "AU").
 * That's why the team sees flags on phones/Macs but plain letters on Windows.
 *
 * We render flags as <img> tags pointing at the Twemoji CDN — same glyphs
 * Twitter / Mastodon / Discord use, identical on every OS, no font needed.
 */
import { memo } from "react";

// Map our internal region codes ("UK", "AU", "AUS", "GB", "NZ") to the
// twemoji filename format (regional indicator pairs as hex codepoints).
const CODE_TO_TWEMOJI = {
  UK:  "1f1ec-1f1e7",   // 🇬🇧
  GB:  "1f1ec-1f1e7",
  AU:  "1f1e6-1f1fa",   // 🇦🇺
  AUS: "1f1e6-1f1fa",
  NZ:  "1f1f3-1f1ff",   // 🇳🇿
  IN:  "1f1ee-1f1f3",   // 🇮🇳
  PH:  "1f1f5-1f1ed",   // 🇵🇭
};

const CODE_TO_NAME = {
  UK: "United Kingdom",
  GB: "United Kingdom",
  AU: "Australia",
  AUS: "Australia",
  NZ: "New Zealand",
  IN: "India",
  PH: "Philippines",
};

// Pinning a major version keeps the URL cache-friendly. jsdelivr serves
// the twitter/twemoji repo directly so we don't ship a dependency.
const TWEMOJI_BASE = "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72";

export const Flag = memo(function Flag({ code, size = 20, className = "", ...rest }) {
  const file = CODE_TO_TWEMOJI[code];
  if (!file) {
    // Unknown code — render a neutral globe so layout doesn't collapse
    return (
      <span
        className={`inline-block align-text-bottom text-base ${className}`}
        style={{ width: size, height: size }}
        aria-label={`region ${code}`}
        {...rest}
      >
        🌐
      </span>
    );
  }
  return (
    <img
      src={`${TWEMOJI_BASE}/${file}.png`}
      alt={CODE_TO_NAME[code] || code}
      title={CODE_TO_NAME[code] || code}
      width={size}
      height={size}
      className={`inline-block align-text-bottom ${className}`}
      style={{ width: size, height: size, objectFit: "contain" }}
      loading="lazy"
      decoding="async"
      {...rest}
    />
  );
});

export default Flag;
